import { FC, Fragment, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getResultAnswers } from 'store/player/playerAsync';
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectPreviewPageMode, selectResult } from 'store/player/playerSelectors';
// Components
import AppBar from 'components/AppBar';
import SocialShare from 'components/SocialShare';
import PublicDevicePlayable from 'components/PublicDevicePlayable';
import QuestionsList from './QuestionsList';
import PoweredBy from 'components/PoweredBy';
import { Button } from 'components/Buttons';
import HiddenResultsPage from 'pages/HiddenResultsPage';
// Icons
import { FiRefreshCcw } from 'react-icons/fi';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { buildUserName } from 'utilities/Utilities';

const ResultsPage:FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug:string }>();

  const dispatch = useAppDispatch();

  const quiz = useAppSelector(selectQuiz);
  const result = useAppSelector(selectResult);
  const previewPageMode = useAppSelector(selectPreviewPageMode);

  const { socialShare } = quiz.resultsPage;

  const spotPrizeWinnerResult = result?.quiz?.spotPrizeWinners?.[0];

  const handleGetResult = () => {
    dispatch(getResultAnswers({ quizName: slug }));
  }

  useEffect(() => {
    if (!previewPageMode || !quiz.resultsPage.enabled) return;
    handleGetResult();
    // eslint-disable-next-line
  }, [previewPageMode]);

  const quizEnded = result?.quiz.endsDate && dayjs().isAfter(Number(result.quiz.endsDate));

  if (!quiz.resultsPage.enabled) return <HiddenResultsPage />;
  if (!result) return null;
  return (
    <div
      className="h-full flex flex-col overflow-y-auto"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') top / cover` : '' }}
    >
      <PublicDevicePlayable />
      <div className="p-4"><AppBar /></div>
      <div className="p-4">
        <div className="mb-2 pt-4 flex justify-center items-center gap-3">
          <h2 className="font-bold text-center text-primary text-2xl">{t('pages.results.title')}</h2>
          {!quizEnded ? (
            <Button onClick={handleGetResult} size="small">
              <FiRefreshCcw />
            </Button>
          ) : null}
        </div>
        {spotPrizeWinnerResult ? (
          <h3 className="mb-2 font-bold text-center text-primary text-sm">
            {`${t('labels.randomPrizeWinner')} - ${buildUserName(spotPrizeWinnerResult)}`}
          </h3>
        ) : null}
        <div className="flex-1 overflow-y-auto">
          <QuestionsList />
        </div>
        {socialShare?.enabled && (socialShare.hashtags || socialShare.url || socialShare.image) ? (
          <Fragment>
            <h3 className="text-center text-sm text-primary font-bold my-2 px-4">{t('pages.results.shareText')}</h3>
            <SocialShare />
          </Fragment>
        ) : null}
        <PoweredBy />
      </div>
    </div>
  )
}

export default ResultsPage;
